import React, { useState } from "react"

import { uploadLinkedInExport } from "@actions/contactActions"
import LinkedInLogo from "@assets/images/newDesign/icons/ndLinkedin.svg"
import { Subtitle, Caption } from "@assets/styles/typographyStyles"
import { ButtonEl } from "@components/common"
import { BUTTON_VARIANTS } from "@constants/button"

import {
  ContactsConnectorItem,
  ConnectorProvider,
  ConnectorIcon,
  LeftSide,
} from "./contactsStyles"
import { LinkedInFileInput } from "./linkedInStyles"

const STATUS = {
  IDLE: "idle",
  UPLOADING: "uploading",
  DONE: "done",
  ERROR: "error",
}

// T-W2-F2 (2026-07-04): LinkedIn Connections-export upload row, rendered as a sibling of the
// Google/Apple ConnectorItem rows above it in Contacts.js. Confirmed live contract against
// api/routers/counterpart_search.py before building (never guessed the inferred `{csv}` key
// from the research doc's flagged unknown -- the real body is `{filename, csv_text}`).
export const LinkedInUploadRow = ({ onUploaded }) => {
  const [status, setStatus] = useState(STATUS.IDLE)
  const [result, setResult] = useState(null)

  const handleFile = async e => {
    const file = e.target.files && e.target.files[0]
    // Reset the input value so re-selecting the SAME file after a failed upload re-fires
    // onChange (browsers otherwise treat an identical selection as a no-op).
    e.target.value = ""
    if (!file) return

    setStatus(STATUS.UPLOADING)
    setResult(null)
    try {
      const csvText = await file.text()
      const data = await uploadLinkedInExport(file.name, csvText)
      setResult(data)
      setStatus(STATUS.DONE)
      if (onUploaded) onUploaded(data)
    } catch (error) {
      console.log("LinkedIn export upload error", error)
      setStatus(STATUS.ERROR)
    }
  }

  return (
    <ContactsConnectorItem isLast={false} data-testid="linkedinUploadRow">
      <LeftSide>
        <ConnectorProvider>
          <ConnectorIcon src={LinkedInLogo} alt="LinkedIn" />
          <Subtitle>Upload your LinkedIn export</Subtitle>
        </ConnectorProvider>

        {status === STATUS.DONE && result && (
          <Caption
            data-testid="linkedinUploadResult"
            style={{ color: "var(--gray)", marginTop: -4 }}>
            {result.matched} matched by email
            {result.proposals > 0
              ? ` · ${result.proposals} pending review`
              : ""}
          </Caption>
        )}
        {status === STATUS.ERROR && (
          <Caption
            data-testid="linkedinUploadError"
            style={{ color: "var(--error, #d0421b)", marginTop: -4 }}>
            Upload failed. Try again.
          </Caption>
        )}
      </LeftSide>

      <LinkedInFileInput>
        <ButtonEl
          autoWidth
          padding="10px 15px"
          backgroundColor="var(--lightGray4)"
          border="1px solid var(--gray3)"
          textColor="var(--black3)"
          loadingColor="var(--lightGray3)"
          variant={BUTTON_VARIANTS.ROUNDED}
          text={status === STATUS.UPLOADING ? "Uploading" : "Upload CSV"}
          isLoading={status === STATUS.UPLOADING}
          disabled={status === STATUS.UPLOADING}
        />
        <input
          data-testid="linkedinExportFileInput"
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          disabled={status === STATUS.UPLOADING}
        />
      </LinkedInFileInput>
    </ContactsConnectorItem>
  )
}
